/**
 * Pure pagination helpers for the Home List View. No Angular, no PrimeNG.
 *
 * Pipeline (see docs/list-view-pagination-analysis.md):
 *   ALL notes → FILTER → SORT → paginate() → current page → table.
 * Pagination only ever sees the already filtered and sorted complete result.
 */

/** Fixed page sizes offered to the user. */
export type PageSizeLimit = 25 | 50 | 100;

/** "No limit": render the complete sorted result (the previous List View behaviour). */
export const UNLIMITED = 'unlimited' as const;
export type Unlimited = typeof UNLIMITED;

export type PageSize = PageSizeLimit | Unlimited;

export const PAGE_SIZE_OPTIONS: readonly PageSize[] = [25, 50, 100, UNLIMITED];
export const DEFAULT_PAGE_SIZE: PageSize = 50;

/** Versioned, device-local preference (same family as the List sort / table-state keys). */
export const LIST_PAGE_SIZE_STATE_KEY = 'notesapp.home.list.pageSize.v1';

/** One page of a complete result, plus everything the UI needs to describe it. */
export interface PageResult<T> {
  items: T[];
  /** 1-based, already clamped to the valid range. */
  page: number;
  /** At least 1, even for an empty result. */
  totalPages: number;
  /** Size of the COMPLETE result, not of the page. */
  totalRecords: number;
  pageSize: PageSize;
  /** 0-based offset of the first item (PrimeNG's `first`). */
  first: number;
  /** 1-based position of the first / last item shown (both 0 for an empty result). */
  rangeStart: number;
  rangeEnd: number;
}

export function isUnlimited(size: PageSize): size is Unlimited {
  return size === UNLIMITED;
}

export function isPageSize(value: unknown): value is PageSize {
  return (PAGE_SIZE_OPTIONS as readonly unknown[]).includes(value);
}

/** Accepts only the exact stored spellings ('25' | '50' | '100' | 'unlimited'); anything else is invalid. */
export function parsePageSize(raw: string | null | undefined): PageSize | null {
  if (raw === UNLIMITED) return UNLIMITED;
  if (raw === '25' || raw === '50' || raw === '100') return Number(raw) as PageSizeLimit;
  return null;
}

/** Minimal Storage surface so this stays testable and independent of the browser. */
export type PageSizeStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** Reads and validates the stored preference; missing, invalid or unreadable storage falls back to the default. */
export function readStoredPageSize(storage: PageSizeStorage | null | undefined): PageSize {
  try {
    return parsePageSize(storage?.getItem(LIST_PAGE_SIZE_STATE_KEY)) ?? DEFAULT_PAGE_SIZE;
  } catch {
    return DEFAULT_PAGE_SIZE;
  }
}

/** Persists the preference immediately. Returns false when storage is unavailable (the in-memory choice still applies). */
export function writeStoredPageSize(storage: PageSizeStorage | null | undefined, size: PageSize): boolean {
  try {
    storage?.setItem(LIST_PAGE_SIZE_STATE_KEY, String(size));
    return !!storage;
  } catch {
    return false;
  }
}

/** Rows per page for the given result size; "No limit" means one page holding everything. */
export function rowsPerPage(size: PageSize, totalRecords: number): number {
  return isUnlimited(size) ? Math.max(totalRecords, 1) : size;
}

/** Number of pages, never below 1 (an empty result still has one, empty, page). */
export function totalPages(totalRecords: number, size: PageSize): number {
  const total = Math.max(0, Math.floor(totalRecords) || 0);
  return Math.max(1, Math.ceil(total / rowsPerPage(size, total)));
}

/** Clamps a 1-based page to [1, totalPages]; NaN and fractions are normalised. */
export function clampPage(page: number, totalRecords: number, size: PageSize): number {
  const wanted = Number.isFinite(page) ? Math.floor(page) : 1;
  return Math.min(Math.max(wanted, 1), totalPages(totalRecords, size));
}

/** Returns the requested page of `items` (which must already be filtered AND sorted). Never mutates `items`. */
export function paginate<T>(items: readonly T[], page: number, size: PageSize): PageResult<T> {
  const totalRecords = items.length;
  const current = clampPage(page, totalRecords, size);
  const perPage = rowsPerPage(size, totalRecords);
  const first = (current - 1) * perPage;
  const pageItems = items.slice(first, first + perPage);
  return {
    items: pageItems,
    page: current,
    totalPages: totalPages(totalRecords, size),
    totalRecords,
    pageSize: size,
    first,
    rangeStart: pageItems.length ? first + 1 : 0,
    rangeEnd: pageItems.length ? first + pageItems.length : 0,
  };
}

/**
 * Result count text, always about the COMPLETE matching result:
 * "1–50 of 1,664 notes", "51–100 of 137 notes", "1,664 notes" (No limit), "0 notes", "1 note".
 */
export function formatResultCount(
  result: Pick<PageResult<unknown>, 'totalRecords' | 'pageSize' | 'rangeStart' | 'rangeEnd'>,
  locale: string = 'en-US',
): string {
  const fmt = (n: number) => n.toLocaleString(locale);
  const noun = result.totalRecords === 1 ? 'note' : 'notes';
  if (result.totalRecords === 0 || isUnlimited(result.pageSize) || result.totalRecords === 1) {
    return `${fmt(result.totalRecords)} ${noun}`;
  }
  return `${fmt(result.rangeStart)}–${fmt(result.rangeEnd)} of ${fmt(result.totalRecords)} ${noun}`;
}
